import { useState, useRef } from "react";

import { Box, List, Stack } from "@mui/material";

import { JumpToSideBarItem } from "./JumpToSideBarItem";
import { NavigationItem } from "./types";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { LIVE_EDITOR_ANIMATION } from "@/components/LiveEditorElements/liveEditorAnimation";
import { LIVE_FORM_Z_INDEX } from "@/components/LiveEditorElements/liveFormZIndex";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { ArrowUp } from "@/icons/outlined";

export type { NavigationItem } from "./types";

export interface JumpToSideBarProps {
  isOpen: boolean;
  onClose: () => void;
  items: NavigationItem[];
  onItemClick: (itemId: string, onNavigationComplete: () => void) => void;
}

export const JumpToSideBar = ({ isOpen: isOpenProp, onClose, items, onItemClick }: JumpToSideBarProps) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const [isNavigationOpenFromHover, setIsNavigationOpenFromHover] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const isOpen = isOpenProp || isNavigationOpenFromHover;

  const resetScrollPositionOfContent = () => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  };

  const handleMouseLeave = () => {
    if (!isOpenProp) {
      resetScrollPositionOfContent();
      setIsNavigationOpenFromHover(false);
    }
  };

  const handleMouseEnter = () => {
    if (!isOpenProp) {
      resetScrollPositionOfContent();
      setIsNavigationOpenFromHover(true);
    }
  };

  const forceCloseNavigation = () => {
    setIsNavigationOpenFromHover(false);
    onClose();
  };

  const handleItemClick = (itemId: string) => {
    onItemClick(itemId, forceCloseNavigation);
  };

  return (
    <>
      {/* Hover trigger area */}
      <Box
        onMouseEnter={handleMouseEnter}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          ["@supports (height: 100dvh)"]: {
            height: "100dvh",
          },
          width: 5,
          zIndex: LIVE_FORM_Z_INDEX.SIDEBAR_OVERLAY,
        }}
        data-test="jump-to-sidebar-overlay-hover-trigger"
      />

      {/* Overlay */}
      <Box
        sx={(theme) => ({
          position: "fixed",
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          backgroundColor: theme.palette.overlay.neutral.moderate,
          visibility: isOpen && !isSmallScreen ? "visible" : "hidden",
          transition: LIVE_EDITOR_ANIMATION.sidebarTransition,
          zIndex: LIVE_FORM_Z_INDEX.SIDEBAR_OVERLAY,
        })}
        onClick={onClose}
        data-test="jump-to-sidebar-overlay"
      />

      {/* Sidebar */}
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          ["@supports (height: 100dvh)"]: {
            height: "100dvh",
          },
          width: { xs: "100vw", md: 312 },
          transform: { xs: `translateY(${isOpen ? 0 : "-100%"})`, md: `translateX(${isOpen ? 0 : "-100%"})` },
          transition: LIVE_EDITOR_ANIMATION.sidebarTransition,
          backgroundColor: "background.paper",
          borderRight: (theme) => (isOpen ? `1px solid ${theme.palette.divider}` : "none"),
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          zIndex: LIVE_FORM_Z_INDEX.SIDEBAR_NAVIGATION,
        }}
        data-test="jump-to-side-bar"
      >
        <Stack
          direction="column"
          justifyContent="space-between"
          sx={{
            height: "100vh",
            ["@supports (height: 100dvh)"]: {
              height: "100dvh",
            },
          }}
        >
          {/* Content (scrollable) */}
          <Box
            ref={contentRef}
            data-test="editor-navigation-content"
            sx={{
              overflow: "auto",
              flexGrow: 1,
              padding: 3,
            }}
          >
            <DashboardTypography variant="subtitle1" sx={{ marginTop: { xs: 0, md: 3 } }}>
              {t("dashboard", "jumpTo")}
            </DashboardTypography>
            {/* Navigation actions */}
            <List>
              {items.map((item) => (
                <JumpToSideBarItem key={item.id} item={item} onItemClick={handleItemClick} />
              ))}
            </List>
          </Box>

          {/* Footer with back to dashboard and share form button */}
          {isSmallScreen && (
            <Stack
              direction="row"
              flexWrap="wrap"
              sx={{
                position: "sticky",
                bottom: 0,
                backgroundColor: "background.paper",
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                justifyContent: "center",
                alignItems: "center",
                paddingX: 3,
                paddingY: 2,
              }}
              data-test="jump-to-sidebar-mobile-footer"
            >
              <DashboardButton
                onClick={forceCloseNavigation}
                variant="text"
                size="small"
                vibe="neutral"
                data-test="jump-to-sidebar-bottom-bar-close"
                startIcon={<ArrowUp />}
              >
                {t("dashboard", "liveFormEditor.sidebar.backToEditing")}
              </DashboardButton>
            </Stack>
          )}
        </Stack>
      </Box>
    </>
  );
};
